import React from 'react';
import { cn } from '../../utils/cn';

/**
 * Canonical surface card — ONE radius, ONE border/background pair, and a fixed padding scale, so
 * every card in the app shares the same shell instead of each component hand-rolling
 * `rounded-2xl border ... p-4` (which had drifted to 2xl/xl/lg + p-2/p-3/p-5 across ~8 files).
 * Vary the inset with `padding`; pass any other layout via `className`. Monochrome by default.
 */
export const CARD_PADDING = { none: '', sm: 'p-3', md: 'p-4', lg: 'p-5' } as const;

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Inset scale — default 'md' (p-4). Use 'lg' for prominent modals, 'sm' for dense rows. */
  padding?: keyof typeof CARD_PADDING;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ padding = 'md', className, children, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-xl border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-900',
        CARD_PADDING[padding],
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  ),
);
Card.displayName = 'Card';

export default Card;
